import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";
import { HelpCircle } from "lucide-react";
import { Card } from "@/components/Card/Card";
import {
  Tooltip as UITooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { ROIByMonthChart as ROIByMonthChartType } from "../../api/fetchROIDashboardData/types";

interface ROIByMonthChartProps {
  data: ROIByMonthChartType | null;
}

const formatYAxis = (value: number): string => {
  return `$${value}`;
};

const formatTooltipValue = (value: number): string => {
  return `$${value.toLocaleString()}`;
};

const ROIByMonthChart: React.FC<ROIByMonthChartProps> = ({ data }) => {
  if (!data) {
    return null;
  }

  return (
    <Card className="w-full">
      <div className="p-6">
        <div className="flex items-center gap-2 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
            ROI by Month
          </h3>
          <TooltipProvider>
            <UITooltip>
              <TooltipTrigger>
                <HelpCircle className="h-4 w-4 text-gray-400 cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <p>
                  Return on investment per dollar spent on direct mail marketing
                  for each month in {data.year}. Calculated as matchback revenue
                  divided by marketing spend.
                </p>
              </TooltipContent>
            </UITooltip>
          </TooltipProvider>
        </div>

        <div className="h-80">
          <ResponsiveContainer height="100%" width="100%">
            <BarChart
              data={data.data}
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" vertical={false} />
              <XAxis
                axisLine={false}
                dataKey="month"
                tick={{ fill: "#6B7280", fontSize: 12 }}
                tickLine={false}
              />
              <YAxis
                axisLine={false}
                tick={{ fill: "#6B7280", fontSize: 12 }}
                tickFormatter={formatYAxis}
                tickLine={false}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: "white",
                  border: "1px solid #E5E7EB",
                  borderRadius: "8px",
                  boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                }}
                formatter={(value: number) => [
                  formatTooltipValue(value),
                  "ROI",
                ]}
                labelStyle={{ fontWeight: 600, marginBottom: 4 }}
              />
              <Bar
                dataKey="roi"
                fill="#8B5CF6"
                name="ROI"
                radius={[4, 4, 0, 0]}
              />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </Card>
  );
};

export default ROIByMonthChart;
